import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown } from 'react-icons/fa';
import { scrollToSection } from '../../utils/commonUtils';
import cloudiAIcon from '../../assets/images/icons/CloudiA_icon.svg';
import synergiAIcon from '../../assets/images/icons/SynergiA_icon.svg';
import geniAIcon from '../../assets/images/icons/GeniA_icon.svg';
import solution_bg from '../../assets/images/solution_bg.avif';
import solution_banner from '../../assets/images/solution_banner.avif';
import cloudiALogo from '../../assets/images/logo/CloudiA_logo.svg';
import synergiALogo from '../../assets/images/logo/SynergiA_logo.svg';
import geniALogo from '../../assets/images/logo/GeniA_logo.svg';
import ActionButton from '../common/ActionButton';

export default function SolutionSection() {
  const [openIndex, setOpenIndex] = useState<boolean[]>([false, false, false]);
  const { t } = useTranslation();
  const FOLDING_DELAY = 0;

  const solutionsOverview = [
    {
      name: 'CloudiA',
      title: t('Solution.overview.cloudiA.title'),
      description: t('Solution.overview.cloudiA.description'),
      image: cloudiALogo,
    },
    {
      name: 'SynergiA',
      title: t('Solution.overview.synergiA.title'),
      description: t('Solution.overview.synergiA.description'),
      image: synergiALogo,
    },
    {
      name: 'GeniA',
      title: t('Solution.overview.geniA.title'),
      description: t('Solution.overview.geniA.description'),
      image: geniALogo,
    },
  ];

  const solutionsDetail = [
    {
      name: 'CloudiA',
      title1: t('Solution.CloudiA.title1'),
      title2: t('Solution.CloudiA.title2'),
      details: t('Solution.CloudiA.details'),
      icon: cloudiAIcon,
      cards: [
        {
          title: t('Solution.CloudiA.cards.0.title'),
          description: t('Solution.CloudiA.cards.0.description'),
        },
        {
          title: t('Solution.CloudiA.cards.1.title'),
          description: t('Solution.CloudiA.cards.1.description'),
        },
        {
          title: t('Solution.CloudiA.cards.2.title'),
          description: t('Solution.CloudiA.cards.2.description'),
        },
      ],
    },
    {
      name: 'SynergiA',
      title1: t('Solution.SynergiA.title1'),
      title2: t('Solution.SynergiA.title2'),
      details: t('Solution.SynergiA.details'),
      icon: synergiAIcon,
      cards: [
        {
          title: t('Solution.SynergiA.cards.0.title'),
          description: t('Solution.SynergiA.cards.0.description'),
        },
        {
          title: t('Solution.SynergiA.cards.1.title'),
          description: t('Solution.SynergiA.cards.1.description'),
        },
        {
          title: t('Solution.SynergiA.cards.2.title'),
          description: t('Solution.SynergiA.cards.2.description'),
        },
      ],
    },
    {
      name: 'GeniA',
      title1: t('Solution.GeniA.title1'),
      title2: t('Solution.GeniA.title2'),
      details: t('Solution.GeniA.details'),
      icon: geniAIcon,
      cards: [
        {
          title: t('Solution.GeniA.cards.0.title'),
          description: t('Solution.GeniA.cards.0.description'),
        },
        {
          title: t('Solution.GeniA.cards.1.title'),
          description: t('Solution.GeniA.cards.1.description'),
        },
        {
          title: t('Solution.GeniA.cards.2.title'),
          description: t('Solution.GeniA.cards.2.description'),
        },
      ],
    },
  ];

  const handleScrollToSolutionsDetail = (name: string, index: number) => {
    setOpenIndex(() => {
      const newOpenIndex = [false, false, false];
      newOpenIndex[index] = true;
      return newOpenIndex;
    });
    setTimeout(() => {
      scrollToSection(`solution-${name}`);
    }, FOLDING_DELAY);
  };

  const handleOpenSolutionDetail = (index: number) => {
    const newOpenIndex = [...openIndex];
    newOpenIndex[index] = !newOpenIndex[index];
    setOpenIndex(newOpenIndex);
  };

  return (
    <section id="solution" className="min-h-screen flex flex-col justify-center bg-grayBack">
      <div
        className="w-full py-[100px] bg-cover bg-center bg-no-repeat flex flex-col justify-center"
        style={{ backgroundImage: `url(${solution_bg})` }}
      >
        <div className="w-full max-w-[1280px] mx-auto px-6">
          <div className="flex justify-center items-center">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-primary">
              Solutions
            </h2>
          </div>
          <h1 className="text-base text-lg md:text-3xl font-bold text-center mt-8 text-white leading-relaxed">
            <div>
              {t('Solution.mainTitle1')}
              <div className="block lg:hidden" />
              {t('Solution.mainTitle2')}
            </div>
            <div>
              {t('Solution.mainTitle3')}
              <div className="block lg:hidden" />
              {t('Solution.mainTitle4')}
            </div>
          </h1>
          <div className="flex justify-center mt-10">
            <ActionButton
              label={t('common.contact')}
              onClick={() => scrollToSection('contact')}
              type="orange"
            />
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-6">
        <div className="w-full mb-20 flex flex-col space-y-6">
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6 mb-20 w-full">
            {solutionsOverview.map((solution, index) => (
              <button
                key={index}
                className="lg:h-[400px] h-[300px] bg-white rounded-[10px] transition-all duration-300 hover:scale-[103%]"
                onClick={() => handleScrollToSolutionsDetail(solution.name, index)}
              >
                <div className="flex flex-col items-center justify-evenly h-full">
                  <img src={solution.image} className="w-[280px] md:h-[131px] h-[100px]" />
                  <div>
                    <h3 className="text-p1 font-bold">{solution.title}</h3>
                    <p className="font-light text-center leading-relaxed tracking-wide">
                      {solution.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {solutionsDetail.map((solution, index) => (
            <button
              id={`solution-${solution.name}`}
              key={index}
              onClick={() => handleOpenSolutionDetail(index)}
              className={`text-white font-bold shadow-md rounded-lg overflow-hidden transition-all duration-300 ease-in-out ${
                openIndex[index] ? 'bg-productCard' : 'bg-productCard hover:bg-[#303f52]'
              }`}
            >
              <div className="px-6 py-8 md:px-16 md:py-10 flex items-center justify-between transition duration-200 gap-4">
                <div className="flex gap-8 items-center flex-1">
                  <div className="flex flex-col lg:flex-row items-center gap-6 w-full">
                    <div className="flex items-center gap-4">
                      <img src={solution.icon} alt="Icon" className="w-8 md:w-16 flex-shrink-0" />
                      <h3 className="text-xl md:text-3xl font-thin text-left font-[KOHIBaeumOTF]">
                        {solution.name}
                      </h3>
                    </div>
                    <div className="hidden lg:block w-[2px] h-10 bg-gray-500 rounded-lg" />
                    <h3 className="text-xl md:text-3xl font-bold text-left text-center">
                      {solution.title1} <div className="block md:hidden" />
                      {solution.title2}
                    </h3>
                  </div>
                </div>
                <span
                  className={`transition-transform ${openIndex[index] ? 'rotate-180' : 'rotate-0'}`}
                >
                  <FaChevronDown className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
                </span>
              </div>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openIndex[index]
                    ? 'flex flex-col gap-10 opacity-100 p-8 md:p-16 bg-productCard border-t border-gray-500'
                    : 'max-h-0 opacity-0 hidden px-0 py-0'
                }`}
              >
                <p className="text-gray-300 font-light sm:text-p6 md:text-p4 leading-relaxed tracking-wide text-left">
                  {solution.details}
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {solution.cards.map((card, cardIndex) => (
                    <div
                      key={cardIndex}
                      className="bg-grayBack p-6 md:px-8 md:py-16 rounded-lg shadow-md text-white text-left"
                    >
                      <h4 className="text-base md:text-2xl font-semibold mb-4">{card.title}</h4>
                      <p className="text-gray-300 font-normal md:text-lg leading-relaxed break-keep">
                        {card.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div
        className="w-full py-[100px] flex flex-col justify-center items-center text-center bg-cover bg-center md:bg-fixed bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${solution_banner})`,
        }}
      >
        <h3 className="text-p1 md:text-h3 font-bold px-8 text-white">{t('Solution.finalTitle')}</h3>
        <p className="px-8 mt-6 text-sm md:text-lg font-normal text-white">
          {t('Solution.finalSubtitle1')} <br /> {t('Solution.finalSubtitle2')}
        </p>
      </div>
    </section>
  );
}
